import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Shield, User, Calendar, Hash, Building } from "@/components/icons";
import { changeRole, deleteUser, fetchUserAccess, fetchUserResetAvailability, grantPrime, revokePrime, setUserContact, sendUserReset, setUserDisabled, unlockUser, type ResetChannel, type SystemRole, type UserRecord } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { OrgRoleBadge } from "@/components/OrgRoleBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useResetPassword } from "@/components/ResetPasswordAction";
import { useAuth } from "@/app/AuthContext";
import { formatDuration } from "@voltie/localization-web";
import { useT } from "@/app/localization";
import { errorMessage } from "@/app/serverErrors";
import { viewerInstant } from "@/app/displayFormat";

interface UserDetailPanelProps {
  user: UserRecord;
  isSelf: boolean;
  onDeleted?: () => void;
}

// Lockout time left, in the shared duration format (seconds from the API).
function lockoutLeft(seconds: number): string {
  return formatDuration(Math.max(0, seconds), "seconds");
}

export function UserDetailPanel({ user: u, isSelf, onDeleted }: UserDetailPanelProps) {
  const t = useT();
  const qc = useQueryClient();
  // A read-only admin may VIEW this panel but performs no writes (the backend
  // rejects them). canWrite gates every mutating control below.
  const { isReadOnlyAdmin } = useAuth();
  const canWrite = !isReadOnlyAdmin;

  // After any mutation, refresh BOTH places this panel is rendered from:
  // the Users list query (slide-panel path, kept in sync via allUsers) AND the
  // single-user query behind the standalone /users/:id page. Without the
  // latter, an action on the detail page (e.g. granting Prime) succeeds on the
  // server but the panel keeps showing stale data until a manual reload.
  const invalidateUser = React.useCallback(() => {
    qc.invalidateQueries({ queryKey: ["users"] });
    qc.invalidateQueries({ queryKey: ["user", u.id] });
  }, [qc, u.id]);

  // Temporary free-Prime grant (admin comp). Number of days from now.
  const [primeDays, setPrimeDays] = useState("30");

  // Contact editing
  const [editContact, setEditContact] = useState(false);
  const [emailInput, setEmailInput] = useState(u.email ?? "");
  const [phoneInput, setPhoneInput] = useState(u.phone ?? "");
  React.useEffect(() => {
    // Re-sync the inputs when the selected user refreshes underneath us.
    setEmailInput(u.email ?? "");
    setPhoneInput(u.phone ?? "");
  }, [u.id, u.email, u.phone]);

  // What this user can reach: org memberships + directly assigned units —
  // the user-side mirror of the unit page's "assigned users" list.
  const accessQ = useQuery({
    queryKey: ["user-access", u.id],
    queryFn: () => fetchUserAccess(u.id),
  });

  const roleM = useMutation({
    mutationFn: (newRole: SystemRole) => changeRole(u.id, newRole),
    onSuccess: () => { toast.success(t("operator.users.role_updated")); invalidateUser(); },
    onError: (e) => toast.error(errorMessage(t, e, { fallback: "operator.users.failed" })),
  });

  const deleteM = useMutation({
    mutationFn: () => deleteUser(u.id),
    onSuccess: () => { toast.success(t("operator.users.deleted")); invalidateUser(); onDeleted?.(); },
    onError: (e) => toast.error(errorMessage(t, e, { fallback: "operator.users.failed" })),
  });

  const disableM = useMutation({
    mutationFn: () => setUserDisabled(u.id, !u.disabled),
    onSuccess: () => {
      toast.success(t(u.disabled ? "operator.users.enabled_toast" : "operator.users.disabled_toast"));
      invalidateUser();
    },
    onError: (e) => toast.error(errorMessage(t, e, { fallback: "operator.users.failed" })),
  });

  const unlockM = useMutation({
    mutationFn: () => unlockUser(u.id),
    onSuccess: () => {
      toast.success(t("operator.users.lockout_cleared"));
      invalidateUser();
    },
    onError: (e) => toast.error(errorMessage(t, e, { fallback: "operator.users.failed" })),
  });

  const grantPrimeM = useMutation({
    mutationFn: (days: number) => grantPrime(u.id, days),
    onSuccess: () => {
      toast.success(t("operator.users.prime_granted"));
      invalidateUser();
    },
    onError: (e) => toast.error(errorMessage(t, e, { fallback: "operator.users.prime_grant_failed" })),
  });

  const revokePrimeM = useMutation({
    mutationFn: () => revokePrime(u.id),
    onSuccess: () => {
      toast.success(t("operator.users.prime_revoked"));
      invalidateUser();
    },
    onError: (e) => toast.error(errorMessage(t, e, { fallback: "operator.users.prime_revoke_failed" })),
  });

  const contactM = useMutation({
    mutationFn: () =>
      setUserContact(u.id, { email: emailInput.trim(), phone: phoneInput.trim() }),
    onSuccess: () => {
      toast.success(t("operator.users.contact_updated"));
      setEditContact(false);
      invalidateUser();
    },
    onError: (e) => toast.error(errorMessage(t, e, { family: "user_contact", fallback: "operator.users.contact_update_failed" })),
  });

  // One "Reset password" action: email+phone → masked channel dialog, one method
  // → direct, neither → disabled. The admin never sees the OTP/token/password.
  async function initiateReset(channel: ResetChannel) {
    try {
      await sendUserReset(u.id, channel);
      toast.success(t("operator.users.reset_requested"));
    } catch (e: any) {
      toast.error(errorMessage(t, e, { family: "account_send_reset", fallback: "operator.user_menu.reset_failed" }));
    }
  }
  // Availability + masked destinations come from the backend (authoritative:
  // email present; phone verified + valid E.164 + allowed country).
  const availabilityQ = useQuery({
    queryKey: ["reset-availability", "user", u.id],
    queryFn: () => fetchUserResetAvailability(u.id),
  });
  const avail = availabilityQ.data;
  const reset = useResetPassword({
    emailAvailable: avail?.email_reset_available ?? false,
    smsAvailable: avail?.sms_reset_available ?? false,
    maskedEmail: avail?.masked_email,
    maskedPhone: avail?.masked_phone,
    loading: availabilityQ.isLoading,
    error: availabilityQ.isError,
    initiate: initiateReset,
  });

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3">
          {u.role === "user" ? <User className="h-5 w-5 opacity-60" /> : <Shield className="h-5 w-5 text-primary" />}
          <h2 className="text-xl font-bold">{u.username}</h2>
          {isSelf && <Badge variant="info" className="text-[11px] h-4.5 px-1.5">{t("operator.users.you")}</Badge>}
        </div>
        <div className="mt-2 flex items-center gap-2">
          <Badge variant={u.role === "admin" ? "primary" : u.role === "readonly_admin" ? "info" : "neutral"}>
            {t("operator.user_menu.system_role", { role: u.role })}
          </Badge>
          {u.tier && u.tier !== "free" && (
            <Badge variant="success" className="capitalize">{t("operator.users.tier", { tier: u.tier })}</Badge>
          )}
          {u.prime_grant_active && (
            <Badge variant="info">{t("operator.users.prime_free_badge")}</Badge>
          )}
          {u.disabled && <Badge variant="destructive">{t("operator.users.disabled")}</Badge>}
          {u.locked && (
            <Badge variant="warning">
              {u.lockout_remaining_seconds
                ? t("operator.users.locked_left", { duration: lockoutLeft(u.lockout_remaining_seconds) })
                : t("operator.users.locked")}
            </Badge>
          )}
        </div>
      </div>

      {/* Info */}
      <div className="grid grid-cols-2 gap-3">
        <DetailItem label={t("operator.users.detail_user_id")} value={String(u.id)} />
        <DetailItem label={t("operator.users.column_sessions")} value={String(u.session_count ?? 0)} />
        {/* created_at/last_login are naive server-local timestamps (S-4): display unchanged (LOC-405 report). */}
        <DetailItem label={t("operator.users.column_created")} value={u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"} />
        <DetailItem label={t("operator.users.column_last_login")} value={u.last_login ? new Date(u.last_login).toLocaleString() : t("operator.users.never")} />
        {!!u.failed_attempts && (
          <DetailItem
            label={t("operator.users.detail_failed_logins")}
            value={
              u.locked && u.lockout_remaining_seconds
                ? t("operator.users.failed_logins_locked", { attempts: String(u.failed_attempts), duration: lockoutLeft(u.lockout_remaining_seconds) })
                : String(u.failed_attempts)
            }
          />
        )}
      </div>

      {/* Organizations the user belongs to */}
      <div className="space-y-2">
        <h3 className="text-sm font-semibold opacity-70 uppercase">{t("operator.nav.organizations")}</h3>
        {accessQ.isLoading ? (
          <div className="text-xs opacity-60">{t("common.state.loading")}</div>
        ) : accessQ.isError ? (
          <div className="text-xs text-error">{t("operator.users.memberships_failed")}</div>
        ) : (accessQ.data?.organizations?.length ?? 0) === 0 ? (
          <div className="text-xs opacity-60">{t("operator.users.no_memberships")}</div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {accessQ.data!.organizations.map((o) => (
              <div key={o.organization_id} className="inline-flex items-center gap-1">
                <Link to={`/organizations/${o.organization_id}`}>
                  <Badge variant="default" className="gap-1 cursor-pointer hover:opacity-80">
                    <Building className="h-3 w-3" />
                    {o.organization_name || t("operator.users.org_fallback_name", { id: String(o.organization_id) })}
                  </Badge>
                </Link>
                <OrgRoleBadge role={o.role as "manager" | "driver"} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Units directly assigned to the user */}
      <div className="space-y-2">
        <h3 className="text-sm font-semibold opacity-70 uppercase">
          {accessQ.data
            ? t("operator.users.assigned_units_count", { count: accessQ.data.units.length })
            : t("operator.users.assigned_units")}
        </h3>
        {accessQ.isLoading ? (
          <div className="text-xs opacity-60">{t("common.state.loading")}</div>
        ) : accessQ.isError ? (
          <div className="text-xs text-error">{t("operator.users.units_failed")}</div>
        ) : (accessQ.data?.units?.length ?? 0) === 0 ? (
          <div className="text-xs opacity-60">
            {t("operator.users.no_units")}
          </div>
        ) : (
          <div className="max-h-56 overflow-y-auto space-y-1 pr-1">
            {accessQ.data!.units.map((unit) => {
              // Same custom-name rule as the dashboard table: the backend
              // default `Unit <id>` is not a real name.
              const hasCustomName = !!unit.name && unit.name !== `Unit ${unit.unit_id}`;
              return (
                <Link
                  key={unit.unit_id}
                  to={`/unit/${encodeURIComponent(unit.unit_id)}`}
                  className="flex items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted/60"
                >
                  <span
                    className={`h-2 w-2 shrink-0 rounded-full ${unit.is_online ? "bg-success" : "bg-base-300"}`}
                    title={t(unit.is_online ? "common.status.online" : "common.status.offline")}
                  />
                  {hasCustomName && <span className="truncate font-medium">{unit.name}</span>}
                  <span className="truncate font-mono text-xs opacity-60">{unit.unit_id}</span>
                  {unit.state_name && (
                    <span className="ml-auto shrink-0 text-xs opacity-60">{unit.state_name}</span>
                  )}
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* Contact & recovery */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold opacity-70 uppercase">{t("operator.users.contact_title")}</h3>
          {u.email
            ? (u.email_verified
                ? <Badge variant="success" className="badge-sm">{t("operator.users.filter_email", { status: "verified" })}</Badge>
                : <Badge variant="warning" className="badge-sm">{t("operator.users.filter_email", { status: "unverified" })}</Badge>)
            : <Badge variant="destructive" className="badge-sm">{t("operator.users.filter_email", { status: "missing" })}</Badge>}
        </div>
        {editContact ? (
          <div className="space-y-2">
            <div>
              <div className="text-xs opacity-60 mb-1">{t("operator.users.column_email")}</div>
              <Input
                type="email"
                autoComplete="off"
                placeholder="name@example.com"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
              />
            </div>
            <div>
              <div className="text-xs opacity-60 mb-1">{t("operator.users.phone_label")}</div>
              <Input
                type="tel"
                autoComplete="off"
                placeholder="+15551234567"
                value={phoneInput}
                onChange={(e) => setPhoneInput(e.target.value)}
              />
            </div>
            <div className="text-xs opacity-60">
              {t("operator.users.contact_help")}
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => contactM.mutate()} disabled={contactM.isPending}>
                {contactM.isPending ? t("common.state.saving") : t("operator.users.save_contact")}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setEditContact(false);
                  setEmailInput(u.email ?? "");
                  setPhoneInput(u.phone ?? "");
                }}
              >
                {t("common.action.cancel")}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-3">
              <DetailItem label={t("operator.users.column_email")} value={u.email || "—"} />
              <DetailItem label={t("operator.users.phone_label")} value={u.phone || "—"} />
            </div>
            {canWrite && (
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => setEditContact(true)}>
                  {t(u.email || u.phone ? "operator.users.edit_contact" : "operator.users.add_contact")}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => reset.onTrigger()}
                  disabled={reset.disabled}
                  title={reset.title}
                >
                  {reset.label}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
      {reset.dialog}

      {/* Subscription / Prime */}
      <div className="space-y-2 pt-2 border-t border-border">
        <h3 className="text-sm font-semibold opacity-70 uppercase">{t("operator.users.subscription_title")}</h3>
        <div className="grid grid-cols-2 gap-3">
          <DetailItem
            label={t("operator.users.current_plan")}
            value={t("operator.users.tier", { tier: u.tier || "free" })}
          />
          <DetailItem
            label={t("operator.users.prime_until_label")}
            value={
              // prime_until is an aware UTC instant, shown in the viewer zone.
              (u.prime_grant_active && viewerInstant(u.prime_until)) || "—"
            }
          />
        </div>
        {u.prime_grant_active && u.base_tier && u.base_tier !== "free" && (
          <div className="text-xs opacity-60">
            {t("operator.users.base_plan_note", { tier: u.base_tier })}
          </div>
        )}
        {/* Admins bypass all tier limits, so granting Prime to an admin (or to
            yourself) has no effect — only offer it for non-admin, non-self. */}
        {!isSelf && u.role !== "admin" && canWrite && (
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <div className="text-xs opacity-60 mb-1">{t("operator.users.prime_days")}</div>
              <Input
                type="number"
                min={1}
                max={3650}
                className="w-24"
                value={primeDays}
                onChange={(e) => setPrimeDays(e.target.value)}
              />
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                const days = parseInt(primeDays, 10);
                if (!Number.isFinite(days) || days <= 0 || days > 3650) {
                  toast.error(t("operator.users.prime_days_invalid"));
                  return;
                }
                grantPrimeM.mutate(days);
              }}
              disabled={grantPrimeM.isPending}
            >
              {t(u.prime_grant_active ? "operator.users.prime_extend" : "operator.users.prime_grant_button")}
            </Button>
            {u.prime_grant_active && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  if (confirm(t("operator.users.prime_revoke_confirm", { name: u.username })))
                    revokePrimeM.mutate();
                }}
                disabled={revokePrimeM.isPending}
              >
                {t("operator.users.prime_revoke")}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Admins do not set or see users' passwords: the reset flow above sends
          the user an email/SMS and the user chooses their own new password.
          The legacy direct "Change Password" form was removed for that reason. */}

      {/* Actions */}
      {!isSelf && canWrite && (
        <div className="space-y-2 pt-2 border-t border-border">
          <h3 className="text-sm font-semibold opacity-70 uppercase">{t("operator.users.actions_title")}</h3>
          <div className="flex flex-wrap items-center gap-2">
            {/* Role is a 3-way choice: User / Read-only admin / Admin. A change
                is high-impact in every direction, so confirm before applying. */}
            <div>
              <div className="text-xs opacity-60 mb-1">{t("operator.users.role_label")}</div>
              <Select
                value={u.role}
                onValueChange={(v) => {
                  const next = v as SystemRole;
                  if (next === u.role) return;
                  const msg =
                    next === "admin"
                      ? `Promote "${u.username}" to ADMIN? Admins get full write access to ALL units, users, billing and settings.`
                      : next === "readonly_admin"
                        ? `Make "${u.username}" a READ-ONLY admin? They will see all admin pages and fleet-wide data but cannot make any changes.`
                        : `Change "${u.username}" to a regular user? They will immediately lose access to all admin pages and fleet-wide data.`;
                  if (confirm(msg)) roleM.mutate(next);
                }}
              >
                <SelectTrigger className="w-[180px]" disabled={roleM.isPending}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">{t("operator.user_menu.system_role", { role: "user" })}</SelectItem>
                  <SelectItem value="readonly_admin">Read-only admin</SelectItem>
                  <SelectItem value="admin">{t("operator.user_menu.system_role", { role: "admin" })}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {u.locked && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => unlockM.mutate()}
                disabled={unlockM.isPending}
              >
                {t("operator.users.unlock")}
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                if (u.disabled || confirm(t("operator.users.disable_confirm", { name: u.username })))
                  disableM.mutate();
              }}
              disabled={disableM.isPending}
            >
              {t(u.disabled ? "operator.users.enable_login" : "operator.users.disable_login")}
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => { if (confirm(t("operator.users.delete_confirm", { name: u.username }))) deleteM.mutate(); }}
              disabled={deleteM.isPending}
            >
              {t("operator.users.delete")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-card rounded-lg p-3">
      <div className="text-xs opacity-60">{label}</div>
      <div className="text-sm font-semibold mt-0.5">{value}</div>
    </div>
  );
}
